/** FICHE CLIENTE — la mémoire externalisée de la pro.
 *  Même règle que Jour / Agenda / Clients : une phrase d'abord, les chiffres ensuite. */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Plus, Save } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import {
  Avatar, Btn, Champ, Erreur, Micro, Pastille, ScoreRisque, Squelette, Zone,
} from "../components/Bits";
import { Galerie } from "../components/Photos";
import {
  STATUTS, euros, heure, ilYA, initiales, jourLong, nb, telephoneLisible,
} from "../lib/format";

/* La fiche s'ouvre sur une phrase : qui elle est pour toi, en une ligne. */
function phraseClient(c) {
  if (!c.visites) return "Nouvelle fiche. Aucun rendez-vous honoré pour l'instant.";
  const bouts = [];
  bouts.push(
    `${c.visites} visite${c.visites > 1 ? "s" : ""}, ${euros(c.depense_totale)} au total.`
  );
  if (c.rythme?.jours) bouts.push(`Elle revient tous les ${nb(c.rythme.jours)} jours environ.`);
  if (c.absences > 0) bouts.push(`${c.absences} absence${c.absences > 1 ? "s" : ""} à son actif.`);
  else if (c.jours_depuis !== null && c.jours_depuis !== undefined && c.jours_depuis > 70)
    bouts.push("Elle s'éloigne : un message ne serait pas de trop.");
  return bouts.join(" ");
}

function Chiffre({ micro, valeur, note, testid }) {
  return (
    <div data-testid={testid}>
      <Micro muted>{micro}</Micro>
      <div className="num text-[24px] md:text-[27px] leading-none mt-2">{valeur}</div>
      {note && (
        <div className="text-[12px] mt-1.5" style={{ color: "var(--text-subtle)" }}>
          {note}
        </div>
      )}
    </div>
  );
}

export default function FicheClient() {
  const { id } = useParams();
  const nav = useNavigate();
  const { version, rafraichir, ouvrirRdv } = useApp();
  const [c, setC] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [form, setForm] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setC(data);
      setForm({
        prenom: data.prenom || "",
        nom: data.nom || "",
        phone: data.phone_saisi || data.phone || "",
        email: data.email || "",
        notes: data.notes || "",
        preferences: data.preferences || "",
      });
    } catch (e) {
      setErreur(messageErreur(e, "Fiche introuvable."));
    } finally {
      setCharge(false);
    }
  }, [id]);

  useEffect(() => {
    charger();
  }, [charger, version]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.patch(`/clients/${id}`, {
        prenom: form.prenom.trim(),
        nom: form.nom.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes,
        preferences: form.preferences,
      });
      setC(data);
      toast.success("Fiche mise à jour.");
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  if (charge) return <Squelette lignes={4} />;
  if (erreur)
    return (
      <div>
        <Btn variante="nu" icone={ArrowLeft} onClick={() => nav("/clients")} className="mb-4">
          Retour
        </Btn>
        <Erreur message={erreur} surReessai={charger} />
      </div>
    );

  return (
    <div className="pb-8" data-testid="page-fiche-client">
      <Btn
        variante="nu"
        taille="sm"
        icone={ArrowLeft}
        onClick={() => nav("/clients")}
        data-testid="btn-retour-clients"
      >
        Tous tes clientes
      </Btn>

      {/* ------------------------------------------------------------ entête */}
      <div className="flex items-start gap-4 pt-4">
        <Avatar
          initiales={initiales(c.prenom, c.nom)}
          taille={52}
          ton={c.absences > 0 ? "brique" : c.visites >= 4 ? "olive" : "neutre"}
        />
        <div className="flex-1 min-w-0">
          <h1 className="display text-[27px] md:text-[32px]" data-testid="titre-client">
            {c.prenom} {c.nom || ""}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            {c.risque && <ScoreRisque risque={c.risque} compact />}
            {c.rythme?.jours && (
              <Pastille ton="neutre">Tous les {c.rythme.jours} jours</Pastille>
            )}
            {c.prochain_rdv && <Pastille ton="accent">RDV prévu</Pastille>}
          </div>
        </div>
        <Btn icone={Plus} onClick={() => ouvrirRdv({ client_id: c.id })} data-testid="btn-rdv-client">
          Rendez-vous
        </Btn>
      </div>

      <p className="text-[17px] md:text-[19px] leading-[1.5] mt-5 max-w-[46ch] pose" data-testid="phrase-client">
        {phraseClient(c)}
      </p>

      {/* ----------------------------------------------------------- chiffres */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 mt-8 pt-7"
        style={{ borderTop: "1px solid var(--border)" }}
      >
        <Chiffre micro="Visites" valeur={c.visites} testid="stat-visites" />
        <Chiffre micro="Total dépensé" valeur={euros(c.depense_totale)} testid="stat-depense" />
        <Chiffre micro="Panier moyen" valeur={euros(c.panier_moyen)} testid="stat-panier" />
        <Chiffre
          micro="Dernière visite"
          valeur={
            c.jours_depuis === null || c.jours_depuis === undefined
              ? "—"
              : `${c.jours_depuis} j`
          }
          note={ilYA(c.jours_depuis)}
          testid="stat-derniere"
        />
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-10 mt-10">
        {/* ------------------------------------------------------ historique */}
        <div className="min-w-0 space-y-10">
          <section>
            <div
              className="flex items-baseline justify-between pb-2.5 mb-1"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <Micro>Historique</Micro>
              <span className="num text-[12px]" style={{ color: "var(--text-subtle)" }}>
                {c.historique.length}
              </span>
            </div>
            {c.historique.length === 0 ? (
              <p className="text-[14px] py-5" style={{ color: "var(--text-muted)" }}>
                Aucun rendez-vous pour l'instant.
              </p>
            ) : (
              <div>
                {c.historique.map((h) => (
                  <button
                    key={h.id}
                    onClick={() =>
                      ouvrirRdv({
                        rdv: { ...h, heure: heure(h.start_at), jour: h.start_at.slice(0, 10) },
                      })
                    }
                    data-testid={`histo-${h.id}`}
                    className="w-full text-left flex items-center gap-3 py-3.5"
                    style={{ borderBottom: "1px solid var(--border)" }}
                  >
                    <span className="w-[132px] shrink-0">
                      <span className="block text-[13.5px] font-semibold first-letter:uppercase">
                        {jourLong(h.start_at.slice(0, 10))}
                      </span>
                      <span className="num block text-[12px]" style={{ color: "var(--text-subtle)" }}>
                        {heure(h.start_at)}
                      </span>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14.5px] truncate">{h.service_nom}</span>
                      {h.notes && (
                        <span className="block text-[12px] truncate" style={{ color: "var(--text-subtle)" }}>
                          {h.notes}
                        </span>
                      )}
                    </span>
                    <span className="text-right shrink-0">
                      <span className="num block text-[14.5px]">{euros(h.price)}</span>
                      {h.paid ? (
                        <Pastille ton="olive">Payé</Pastille>
                      ) : (
                        <Pastille ton={STATUTS[h.status]?.ton}>{STATUTS[h.status]?.label}</Pastille>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* --------------------------------------------------- ses photos */}
          <Galerie
            clientId={c.id}
            titre="Ses photos"
            aide="Avant, après, la formule sur l'étiquette. Ce que tu ne veux pas oublier dans six semaines."
          />
        </div>

        {/* -------------------------------------------- colonne de droite */}
        <aside className="space-y-8">
          <section>
            <Micro className="pb-2.5 mb-4 block" style={{ borderBottom: "1px solid var(--border)" }}>
              Coordonnées
            </Micro>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Champ
                  label="Prénom"
                  value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                  data-testid="fiche-prenom"
                />
                <Champ
                  label="Nom"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  data-testid="fiche-nom"
                />
              </div>
              <Champ
                label="Mobile"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                inputMode="tel"
                indice={c.phone ? telephoneLisible(c.phone) : "Sans numéro, pas de rappel"}
                data-testid="fiche-tel"
              />
              <Champ
                label="E-mail"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                inputMode="email"
                data-testid="fiche-email"
              />
              {(c.phone || c.email) && (
                <div className="flex gap-2">
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex-1">
                      <Btn variante="fantome" taille="sm" icone={Phone} className="w-full" data-testid="btn-appeler">
                        Appeler
                      </Btn>
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex-1">
                      <Btn variante="fantome" taille="sm" icone={Mail} className="w-full" data-testid="btn-ecrire">
                        Écrire
                      </Btn>
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>

          <section>
            <Micro className="pb-2.5 mb-4 block" style={{ borderBottom: "1px solid var(--border)" }}>
              Mémoire technique
            </Micro>
            <div className="space-y-3">
              <Zone
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Formule couleur 7.3 + 20 vol, allergie à la résine, ongles fins…"
                data-testid="fiche-notes"
              />
              <Zone
                label="Préférences"
                value={form.preferences}
                onChange={(e) => setForm({ ...form, preferences: e.target.value })}
                placeholder="Préfère le matin, n'aime pas qu'on lui parle, thé vert…"
                data-testid="fiche-preferences"
              />
              <Btn
                icone={Save}
                onClick={enregistrer}
                charge={envoi}
                className="w-full"
                data-testid="btn-enregistrer-fiche"
              >
                Enregistrer la fiche
              </Btn>
            </div>
          </section>

          {c.risque && <ScoreRisque risque={c.risque} />}
        </aside>
      </div>
    </div>
  );
}
